/* eslint-disable @department-of-veterans-affairs/prefer-button-component */

import React from 'react';
import PropTypes from 'prop-types';

import { recordEvent } from '@department-of-veterans-affairs/platform-monitoring/exports';
import { useSelector } from 'react-redux';
import { parsePhoneNumber } from '../../utils/phoneNumbers';

const SearchResult = ({ index, representative }) => {
  const { attributes: repAttributes, id: representativeId } = representative;
  const {
    addressLine1,
    addressLine2,
    addressLine3,
    city,
    stateCode,
    zipCode,
    phone,
    distance,
    email,
    organizationNames: associatedOrgs,
  } = repAttributes;
  const officer = repAttributes.fullName || repAttributes.name;
  const searchQuery = useSelector(state => state.searchQuery);
  const pagination = useSelector(state => state.searchResult.pagination);

  const { contact, extension } = parsePhoneNumber(phone);

  const hasStreetAddress = Boolean((addressLine1 || '').trim());
  const hasCity = Boolean((city || '').trim());
  const hasState = Boolean((stateCode || '').trim());
  const hasZip = Boolean((zipCode || '').trim());
  const hasValidPartialLocation = (hasCity && hasState) || hasZip;

  const isEstimatedAddress = !hasStreetAddress && hasValidPartialLocation;

  const addressExists = hasStreetAddress || hasValidPartialLocation;

  const cityStateText = hasCity && hasState ? `${city}, ${stateCode}` : '';
  const hasDistance =
    distance !== null && distance !== undefined && distance !== '';
  let partialLocationText = zipCode;

  if (cityStateText) {
    partialLocationText = hasZip
      ? `${cityStateText} ${zipCode}`
      : cityStateText;
  }

  const fullLocationText = (
    <>
      {addressLine1}
      {addressLine2 ? (
        <>
          <br /> {addressLine2}
        </>
      ) : null}
      <br />
      {partialLocationText}
    </>
  );

  const destinationAddress = [
    (addressLine1 || '').trim(),
    (addressLine2 || '').trim(),
    (addressLine3 || '').trim(),
    [city, stateCode]
      .filter(Boolean)
      .join(', ')
      .trim(),
    (zipCode || '').trim(),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  // Reusable map href for both full + partial address cases
  const mapHref = `https://maps.google.com?saddr=${
    searchQuery?.context?.location
  }&daddr=${encodeURIComponent(destinationAddress)}`;

  const recordContactLinkClick = () => {
    recordEvent({
      // prettier-ignore
      'event': 'far-search-results-click',
      'search-query': searchQuery.locationQueryString,
      'search-filters-list': {
        'representative-type': searchQuery.representativeType,
        'search-radius':
          searchQuery.committedSearchQuery.searchArea === 'Show all'
            ? 'Show all'
            : `${searchQuery.committedSearchQuery.searchArea} miles`,
        'representative-name': searchQuery.representativeQueryString,
        organization: searchQuery.organization,
      },
      'search-selection': 'Find VA Accredited Rep',
      'search-results-id': representativeId,
      'search-results-total-count': pagination.totalEntries,
      'search-results-total-pages': pagination.totalPages,
      'search-result-position': index,
      'search-result-page': pagination.currentPage,
    });
  };

  const addressAnchorProps = {
    href: mapHref,
    tabIndex: 0,
    className: 'address-anchor',
    onClick: recordContactLinkClick,
    target: '_blank',
    rel: 'noreferrer',
    'aria-label': `${destinationAddress} (opens in a new tab)`,
  };

  return (
    <va-card
      id={`representative-${representativeId}`}
      class="representative-result-card vads-u-padding--4"
    >
      <div className="representative-result-card-content">
        <div className="representative-info-heading">
          <h3
            className="vads-u-margin-top--0"
            aria-describedby={`representative-${representativeId}`}
          >
            {officer && <span className="officer">{officer}</span>}
            {hasDistance && (
              <span className="distance vads-u-margin-top--1 vads-u-display--block vads-u-font-size--h4">
                {parseFloat(JSON.parse(distance).toFixed(2))} miles
                {isEstimatedAddress ? ' (estimated)' : ''}
              </span>
            )}
          </h3>
        </div>

        <div className="associated-organizations-info vads-u-margin-top--1p5">
          {associatedOrgs?.length === 1 && (
            <span className="vads-u-font-size--md">{associatedOrgs[0]}</span>
          )}
          {associatedOrgs?.length > 1 && (
            <va-additional-info
              trigger={`Check organizations for ${officer}`}
              disable-border
              uswds
            >
              {associatedOrgs?.map(org => <div key={org}>{org}</div>)}
            </va-additional-info>
          )}
        </div>

        <div className="representative-contact-section vads-u-margin-top--3">
          {addressExists && (
            <WithIcon iconName="location_on">
              <div className="address-link">
                {isEstimatedAddress && <div>No street address provided</div>}
                <a {...addressAnchorProps}>
                  {isEstimatedAddress ? partialLocationText : fullLocationText}
                </a>
              </div>
            </WithIcon>
          )}
          {contact && (
            <WithIcon iconName="phone" addTopMargin>
              <va-telephone
                contact={contact}
                extension={extension}
                onClick={() => recordContactLinkClick()}
                disable-analytics
              />
            </WithIcon>
          )}
          {email && (
            <WithIcon iconName="mail" addTopMargin>
              <a
                href={`mailto:${email}`}
                onClick={() => recordContactLinkClick()}
                style={{ overflowWrap: 'anywhere' }}
              >
                {email}
              </a>
            </WithIcon>
          )}
        </div>
      </div>
    </va-card>
  );
};

SearchResult.propTypes = {
  representative: PropTypes.object.isRequired,
  index: PropTypes.number,
};

const WithIcon = ({ iconName, children, addTopMargin }) => (
  <div className={addTopMargin ? 'vads-u-margin-top--1p5' : ''}>
    <va-icon class="vads-u-position--absolute" icon={iconName} size={3} />
    <div className="vads-u-margin-left--4">{children}</div>
  </div>
);

WithIcon.propTypes = {
  children: PropTypes.element.isRequired,
  iconName: PropTypes.string.isRequired,
  addTopMargin: PropTypes.bool,
};

export default SearchResult;
